import {
  CalendarOutlined,
  CarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  PercentageOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import { Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';

import { KpiCard } from 'scenes/VisitHistory/components/VisitHistoryKpis';
import { complianceLevel } from 'scenes/VisitHistory/utils/compliance';
import { formatDecimal, formatInt } from 'scenes/VisitHistory/utils/format';

import { StoreVisitsKpis as Kpis } from '../utils/storeVisits';

const I18N_BASE = 'scenes.store_visits.kpis';

interface StoreVisitsKpisProps {
  kpis: Kpis | null;
  /** null mientras el BFF no termina de leer el detalle. */
  observations: number | null;
}

function StoreVisitsKpis({ kpis, observations }: StoreVisitsKpisProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language ?? 'es';

  const value = (text: string | undefined) =>
    text !== undefined ? (
      text
    ) : (
      <Skeleton.Input active style={{ width: 58, height: 38 }} />
    );
  const int = (n: number | undefined) =>
    value(n === undefined ? undefined : formatInt(n, language));
  const pct = (n: number | undefined) =>
    value(n === undefined ? undefined : `${formatDecimal(n, language)}%`);

  return (
    <div className="visit-history-kpis store-visits-kpis">
      <KpiCard
        icon={<CalendarOutlined />}
        label={t(`${I18N_BASE}.planned.label`)}
        value={int(kpis?.planned)}
        hint={t(`${I18N_BASE}.planned.hint`, {
          count: kpis?.pending ?? 0,
        })}
      />
      <KpiCard
        icon={
          <CheckCircleOutlined className="visit-history-kpi__icon--executed" />
        }
        label={t(`${I18N_BASE}.executed.label`)}
        value={int(kpis?.executed)}
        hint={t(`${I18N_BASE}.executed.hint`)}
        executed
      />
      <KpiCard
        icon={
          <ExclamationCircleOutlined className="visit-history-kpi__icon--incident" />
        }
        label={t(`${I18N_BASE}.incidents.label`)}
        value={int(kpis?.incidents)}
        hint={
          observations === null
            ? t(`${I18N_BASE}.incidents.hint_loading`)
            : t(`${I18N_BASE}.incidents.hint`, { count: observations })
        }
      />
      <KpiCard
        icon={<ShopOutlined />}
        label={t(`${I18N_BASE}.coverage.label`)}
        value={pct(kpis?.coveragePct)}
        hint={t(`${I18N_BASE}.coverage.hint`)}
      />
      <KpiCard
        icon={<PercentageOutlined />}
        label={t(`${I18N_BASE}.compliance.label`)}
        value={pct(kpis?.compliancePct)}
        hint={t(`${I18N_BASE}.compliance.hint`)}
        level={kpis ? complianceLevel(kpis.compliancePct) : undefined}
      />
      <KpiCard
        icon={<CarOutlined />}
        label={t(`${I18N_BASE}.routes.label`)}
        value={int(kpis?.routes)}
        hint={t(`${I18N_BASE}.routes.hint`, {
          count: kpis?.distributors ?? 0,
        })}
      />
    </div>
  );
}

export default StoreVisitsKpis;
